import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { useFormikContext } from 'formik';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

import {
  DocumentViewMode,
  readDocumentSettings,
} from 'containers/Tiptap/components/tiptap-extension/document-settings-yjs';
import { useEditorStore } from 'containers/Tiptap/hooks/useEditorStore';

import {
  PreviewCanvas,
  PreviewColumn,
  PreviewFooter,
  PreviewHeader,
  PreviewLabel,
  PreviewPage,
  PreviewPageBody,
  PreviewSampleHeading,
  PreviewSampleText,
  PreviewTitle,
} from './styles';
import { DocumentSettingsFormValues } from './types';

const PAGE_SIZE_MM: Record<string, { widthMm: number; heightMm: number }> = {
  A4: { widthMm: 210, heightMm: 297 },
  A3: { widthMm: 297, heightMm: 420 },
  A5: { widthMm: 148, heightMm: 210 },
  USLetter: { widthMm: 215.9, heightMm: 279.4 },
  USLegal: { widthMm: 215.9, heightMm: 355.6 },
  Tabloid: { widthMm: 279.4, heightMm: 431.8 },
};

const MARGIN_MM: Record<string, number> = {
  narrow: 12.7,
  normal: 25.4,
  wide: 38.1,
};

const PAGE_SIZE_LABEL: Record<string, string> = {
  A4: 'A4',
  A3: 'A3',
  A5: 'A5',
  USLetter: 'US Letter',
  USLegal: 'US Legal',
  Tabloid: 'Tabloid',
};

const MARGIN_LABEL: Record<string, string> = {
  narrow: 'Narrow',
  normal: 'Normal',
  wide: 'Wide',
};

const PREVIEW_TOTAL_PAGES = 3;

const PREVIEW_PAGE_WIDTH_PX = 180;
const SCREEN_PX_PER_MM = 96 / 25.4;

const substitute = (template: string, page: number, total: number): string =>
  template.replace(/\{page\}/g, String(page)).replace(/\{total\}/g, String(total));

// Mirrors Pages' variant selection so the preview reflects what users will see:
// page 1 → first (if differentFirstPage), otherwise the default content.
const pickVariant = (
  pageNum: number,
  defaultContent: string,
  differentFirst: boolean,
  firstContent: string
): string => {
  if (pageNum === 1 && differentFirst) return firstContent;
  return defaultContent;
};

const PreviewPane: React.FC = () => {
  const { values } = useFormikContext<DocumentSettingsFormValues>();
  const { ydoc } = useEditorStore();
  const { widthMm, heightMm } = PAGE_SIZE_MM[values.pageSize] ?? PAGE_SIZE_MM.A4;
  const isLandscape = values.orientation === 'landscape';
  const pageWidthMm = isLandscape ? heightMm : widthMm;
  const pageHeightMm = isLandscape ? widthMm : heightMm;
  const scale = PREVIEW_PAGE_WIDTH_PX / pageWidthMm;
  const fromMm = (mm: number): number => Math.round(mm * scale);
  const fromScreenPx = (px: number): number => Math.round((px / SCREEN_PX_PER_MM) * scale);
  const pageWidth = PREVIEW_PAGE_WIDTH_PX;
  const pageHeight = fromMm(pageHeightMm);
  const marginPx = fromMm(MARGIN_MM[values.margins] ?? MARGIN_MM.normal);

  const label = `${PAGE_SIZE_LABEL[values.pageSize] ?? values.pageSize} · ${
    isLandscape ? 'Landscape' : 'Portrait'
  } · ${MARGIN_LABEL[values.margins] ?? values.margins} margins`;

  // Header/footer content is edited inline and stored in the Y.Doc, not the form.
  // Read it here for a read-only preview reflecting the last saved/synced content.
  const settings = useMemo(() => (ydoc ? readDocumentSettings(ydoc) : null), [ydoc]);
  const differentFirstPage = settings?.header?.differentFirstPage ?? false;

  const headerSanitized = useMemo(
    () => ({
      default: DOMPurify.sanitize(settings?.header?.content ?? ''),
      first: DOMPurify.sanitize(settings?.header?.variants?.first ?? ''),
    }),
    [settings]
  );

  const footerSanitized = useMemo(
    () => ({
      default: DOMPurify.sanitize(settings?.footer?.content ?? ''),
      first: DOMPurify.sanitize(settings?.footer?.variants?.first ?? ''),
    }),
    [settings]
  );

  const sampleBody = (
    <>
      <PreviewSampleHeading>1. Scope</PreviewSampleHeading>
      <PreviewSampleText>
        This risk assessment covers manual handling activities carried out by warehouse staff at the London Distribution
        Centre.
      </PreviewSampleText>
      <PreviewSampleHeading>2. Identified hazards</PreviewSampleHeading>
      <PreviewSampleText>
        Hazards identified through task observation and consultation with operatives.
      </PreviewSampleText>
    </>
  );

  const renderPage = (pageNum: number, total: number): JSX.Element => {
    const headerHTML = pickVariant(pageNum, headerSanitized.default, differentFirstPage, headerSanitized.first);
    const footerHTML = pickVariant(pageNum, footerSanitized.default, differentFirstPage, footerSanitized.first);
    return (
      <PreviewPage style={{ width: pageWidth, height: pageHeight }}>
        <PreviewHeader
          style={{
            paddingLeft: marginPx,
            paddingRight: marginPx,
          }}
          dangerouslySetInnerHTML={{ __html: substitute(headerHTML, pageNum, total) }}
        />
        <PreviewPageBody style={{ paddingLeft: marginPx, paddingRight: marginPx }}>{sampleBody}</PreviewPageBody>
        <PreviewFooter
          style={{
            paddingLeft: marginPx,
            paddingRight: marginPx,
          }}
          dangerouslySetInnerHTML={{ __html: substitute(footerHTML, pageNum, total) }}
        />
      </PreviewPage>
    );
  };

  const isPages = values.viewMode === DocumentViewMode.Pages;

  return (
    <PreviewColumn aria-label="Live preview">
      <PreviewTitle>
        <VisibilityOutlinedIcon fontSize="inherit" />
        Live preview
      </PreviewTitle>
      <PreviewCanvas>
        {isPages ? (
          renderPage(1, PREVIEW_TOTAL_PAGES)
        ) : (
          <PreviewPage style={{ width: pageWidth, minHeight: pageHeight }}>
            <PreviewPageBody style={{ paddingLeft: marginPx, paddingRight: marginPx, minHeight: pageHeight }}>
              {sampleBody}
            </PreviewPageBody>
          </PreviewPage>
        )}
      </PreviewCanvas>
      {isPages && <PreviewLabel>{label}</PreviewLabel>}
    </PreviewColumn>
  );
};

export default PreviewPane;
